import { Workbox } from 'workbox-window';
import { broadcastAdminPwaReload } from './adminPwaReloadChannel.js';
import { isAdminPwaViteDev } from './adminPwaViteDev.js';
import { clearAdminPwaClientState } from './clearAdminPwaClientState.js';

export interface AdminPwaUpdateHandlers {
  readonly setUpdateReady: (ready: boolean) => void;
  readonly setApplyUpdate: (apply: (() => void) | null) => void;
}

export { listenAdminPwaReloadChannel } from './adminPwaReloadChannel.js';

/**
 * Vite DEV does not ship a Workbox SW (`devOptions.enabled: false`). A leftover
 * PROD registration on the same origin can still control navigations (older builds
 * used cache-only `navigateFallback: index.html`; current builds use NetworkFirst
 * document caching). A stale precached shell → blank `#root`, and module requests
 * like `/src/App.tsx` can fail with "Failed to fetch dynamically imported module".
 *
 * Never `register('/sw.js')` in DEV unless Vite serves real classic JS — without
 * a middleware, Vite returns `index.html` for `/sw.js`, and registering HTML as a
 * worker makes recovery worse. Prefer unregister + cache clear; optionally point
 * at the DEV self-destroying `/sw.js` middleware when present.
 */
export const CONTROLLING_RELOAD_FALLBACK_MS = 4_000;

async function disposeStaleAdminServiceWorkerInDev(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const hasController = navigator.serviceWorker.controller != null;
    if (registration == null && !hasController) {
      return;
    }

    // Prefer the DEV kill-switch SW (valid JS from vite middleware). If that
    // fails (old server without middleware), fall back to unregister + reload.
    try {
      const probe = await fetch('/sw.js', { cache: 'no-store', credentials: 'same-origin' });
      const contentType = probe.headers.get('content-type') ?? '';
      if (probe.ok && contentType.includes('javascript')) {
        await navigator.serviceWorker.register('/sw.js', { type: 'classic' });
        return;
      }
    } catch {
      // fall through to hard clear
    }

    await clearAdminPwaClientState();
    if (hasController) {
      window.location.reload();
    }
  } catch {
    try {
      const hadController = navigator.serviceWorker.controller != null;
      await clearAdminPwaClientState();
      if (hadController) {
        window.location.reload();
      }
    } catch {
      // Best-effort — blank-shell recovery must not throw on boot
    }
  }
}

/**
 * Registers the production admin service worker (online-first; prompt updates).
 */
export function registerAdminPwaServiceWorker(handlers: AdminPwaUpdateHandlers): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  if (isAdminPwaViteDev()) {
    void disposeStaleAdminServiceWorkerInDev();
    return;
  }

  // Bypass HTTP cache on SW script fetches (RegistrationOptions.updateViaCache).
  // Speeds update *detection* after deploy; does not mean soft F5 / NetworkFirst
  // alone heals while a pre-G1 / waiting SW still controls — use Apply,
  // Settings→Update, or Clear site data (skipWaiting: false).
  const workbox = new Workbox('/sw.js', { type: 'classic', updateViaCache: 'none' });

  workbox.addEventListener('waiting', () => {
    let reloadScheduled = false;
    const reloadOnce = (): void => {
      if (reloadScheduled) {
        return;
      }
      reloadScheduled = true;
      broadcastAdminPwaReload();
    };

    const refresh = (): void => {
      workbox.addEventListener('controlling', () => {
        reloadOnce();
      });
      void workbox.messageSkipWaiting();
      // Early nudge — controlling may already be pending after user clicks Apply.
      window.setTimeout(reloadOnce, 1_000);
      window.setTimeout(reloadOnce, CONTROLLING_RELOAD_FALLBACK_MS);
    };

    handlers.setUpdateReady(true);
    // Double arrow: React setState treats a function arg as an updater.
    handlers.setApplyUpdate(() => refresh);
  });

  void workbox.register().catch(() => {
    // Missing SW must not break boot
  });
}
